// One fine: what the notice says, who might have been driving and why, and the
// decisions a manager can record. Every rule (who may decide, what a status
// needs, what history may not change) is enforced by the fine_* functions; the
// screen shows the same rule beforehand so nobody presses a button to learn it.
//
// A PROPOSAL IS NOT A NAME ON A FINE. The candidates are shown with the method
// that found them and the reason in words, and nothing moves until a manager
// presses Confirm. A driver's name on a fine can cost them their weekly prize,
// so the button says what it does.
import { useState, type ReactNode } from 'react'
import { ArrowLeft, ExternalLink, History, ShieldAlert } from 'lucide-react'
import { Button } from '../../lib/shadcn/button'
import { Input } from '../../lib/shadcn/input'
import { Textarea } from '../../lib/shadcn/textarea'
import { NativeSelect } from '../../lib/shadcn/native-select'
import { Spinner } from '../../lib/shadcn/spinner'
import { Chip } from '../interviews/Chip'
import { FineOffenceSection } from './FineOffenceSection'
import {
  useConfirmDriver,
  useDeleteFine,
  useGetEvidenceUrl,
  useSetChargedTo,
  useSetFineStatus,
} from '../../hooks/backend/fines'
import {
  aartoLabel,
  chargedToLabel,
  eventLabel,
  formatDay,
  formatMonth,
  formatSastDateTime,
  formatZar,
  methodLabel,
  NOT_RECORDED,
  ownerBasisLabel,
  sourceLabel,
  statusLabel,
  statusTone,
  whenLabel,
  type FineDetail,
  type Options,
} from '../../utils/fines'

function Fact({ label, children }: { label: string; children: ReactNode }) {
  return (
    <div className="min-w-0">
      <dt className="text-[11px] uppercase tracking-wide text-muted-foreground">{label}</dt>
      <dd className="break-words text-sm text-foreground">{children}</dd>
    </div>
  )
}

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section className="space-y-2 border-t border-border px-4 py-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</h3>
      {children}
    </section>
  )
}

export function FinePanel({
  detail,
  loading,
  error,
  options,
  aartoFlag,
  onBack,
  onChanged,
  onDeleted,
}: {
  detail: FineDetail | null
  aartoFlag: 'red' | 'overdue' | null
  loading: boolean
  error: string | null
  options: Options | null
  onBack: () => void
  onChanged: (d: FineDetail) => void
  onDeleted: () => void
}) {
  const confirm = useConfirmDriver<FineDetail>()
  const setStatus = useSetFineStatus<FineDetail>()
  const setCharged = useSetChargedTo<FineDetail>()
  const del = useDeleteFine<{ deleted: boolean; evidence_removed: boolean | null }>()
  const evidence = useGetEvidenceUrl<{ url: string }>()

  const [otherDriver, setOtherDriver] = useState('')
  const [note, setNote] = useState('')
  const [statusNote, setStatusNote] = useState('')
  const [paidAmount, setPaidAmount] = useState('')
  const [paidOn, setPaidOn] = useState('')
  const [deleteReason, setDeleteReason] = useState('')
  const [message, setMessage] = useState<{ tone: 'ok' | 'error'; text: string } | null>(null)

  if (!detail) {
    return (
      <div className="flex h-full flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
        {loading ? (
          <span className="flex items-center gap-2"><Spinner className="h-4 w-4" /> Opening the fine…</span>
        ) : error ? (
          <span className="text-rose-700 dark:text-rose-300">{error}</span>
        ) : (
          'Choose a fine from the list, or capture a new notice.'
        )}
      </div>
    )
  }

  const f = detail.fine
  const busy = confirm.loading || setStatus.loading || setCharged.loading || del.loading
  // The flag is the queue's (fines_queue_v1), never worked out here.
  const aarto = aartoLabel({ aarto_flag: aartoFlag, days_since_offence: f.days_since_offence })
  const blocked = detail.conflict

  const run = async (p: Promise<FineDetail | null | undefined>, ok: string) => {
    setMessage(null)
    try {
      const d = await p
      if (d) onChanged(d)
      setMessage({ tone: 'ok', text: ok })
      setNote('')
      setStatusNote('')
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : String(e) })
    }
  }

  const openEvidence = async () => {
    setMessage(null)
    try {
      const r = await evidence.trigger({ id: f.id }, { skipCache: true })
      if (r?.url) window.open(r.url, '_blank', 'noopener')
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : String(e) })
    }
  }

  const onDelete = async () => {
    setMessage(null)
    try {
      await del.trigger({ id: f.id, reason: deleteReason })
      onDeleted()
    } catch (e) {
      setMessage({ tone: 'error', text: e instanceof Error ? e.message : String(e) })
    }
  }

  const liveCandidates = detail.candidates.filter((c) => c.driver_id)
  const otherChoices = (options?.drivers ?? []).filter((d) => !liveCandidates.some((c) => c.driver_id === d.id))

  return (
    <div className="flex min-h-0 flex-1 flex-col overflow-y-auto">
      <div className="flex items-start gap-3 border-b border-border px-4 py-3">
        <Button size="icon" variant="ghost" className="md:hidden" onClick={onBack} aria-label="Back to the list">
          <ArrowLeft className="h-4 w-4" />
        </Button>
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <h2 className="font-mono text-lg font-semibold text-foreground">{f.registration ?? 'No vehicle'}</h2>
            <span className="text-lg font-semibold tabular-nums">{formatZar(f.amount_zar)}</span>
            <Chip tone={statusTone(f.status)}>{statusLabel(f.status)}</Chip>
            {f.source !== 'manual' && <Chip tone="neutral" icon={<History className="h-3 w-3" />}>{sourceLabel(f.source)}</Chip>}
          </div>
          <p className="text-xs text-muted-foreground">
            {f.notice_number ? `Notice ${f.notice_number}` : 'No notice number'}
            {f.issuing_authority ? `, ${f.issuing_authority}` : ''}
          </p>
          {aarto && <p className="mt-1 text-xs font-medium text-rose-700 dark:text-rose-300">{aarto}</p>}
        </div>
        {f.evidence_path && (
          <Button size="sm" variant="outline" onClick={() => void openEvidence()} disabled={evidence.loading}>
            <ExternalLink className="mr-1 h-3.5 w-3.5" /> Open the notice
          </Button>
        )}
      </div>

      {message && (
        <div className={message.tone === 'ok'
          ? 'border-b border-border bg-emerald-50 px-4 py-2 text-sm text-emerald-900 dark:bg-emerald-950 dark:text-emerald-100'
          : 'border-b border-border bg-rose-50 px-4 py-2 text-sm text-rose-800 dark:bg-rose-950 dark:text-rose-200'}>
          {message.text}
        </div>
      )}

      <dl className="grid grid-cols-1 gap-3 px-4 py-3 sm:grid-cols-2">
        <Fact label="When">{whenLabel(f)}</Fact>
        <Fact label="Where">{f.location ?? NOT_RECORDED}</Fact>
        <Fact label="Offence">{f.offence_description ?? NOT_RECORDED}</Fact>
        <Fact label="Discount until">{f.discount_deadline ? formatDay(f.discount_deadline) : NOT_RECORDED}</Fact>
        <Fact label="Owner">
          {f.owner_name ?? NOT_RECORDED}
          <span className="block text-xs text-muted-foreground">{ownerBasisLabel(f.owner_basis)}</span>
        </Fact>
        <Fact label="Who carries it">
          {chargedToLabel(f.charged_to)}
          {f.charged_to_decided_by_name && (
            <span className="block text-xs text-muted-foreground">
              {f.charged_to_decided_by_name}, {formatSastDateTime(f.charged_to_decided_at)}
            </span>
          )}
        </Fact>
      </dl>

      <FineOffenceSection key={f.id} detail={detail} onChanged={onChanged} />

      {f.notes && (
        <p className="mx-4 mb-3 rounded-md border border-border bg-muted/40 px-3 py-2 text-xs text-muted-foreground">{f.notes}</p>
      )}
      {detail.statement_line && (
        <p className="mx-4 mb-3 text-xs text-muted-foreground">
          On the {detail.statement_line.issued ? 'issued' : 'draft'} owner statement for {formatMonth(detail.statement_line.period)}, line of {formatZar(detail.statement_line.amount_zar)}.
          {detail.statement_line.issued ? ' Issued statements are final and this screen never changes them.' : ''}
        </p>
      )}
      {detail.payment && <p className="mx-4 mb-3 text-xs text-muted-foreground">{detail.payment.notes}</p>}
      {detail.reversals.map((r) => (
        <p key={r.id} className="mx-4 mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          {r.notes}
        </p>
      ))}
      {detail.possible_duplicates.length > 0 && (
        <p className="mx-4 mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-100">
          Another notice on this vehicle has exactly the same date and time ({detail.possible_duplicates.map((d) => d.notice_number ?? 'no number').join(', ')}). Check they are different notices.
        </p>
      )}

      <Section title="Who was driving">
        {f.driver_id || f.confirmed_at ? (
          <div className="rounded-md border border-border bg-card px-3 py-2 text-sm">
            <p className="font-medium">{f.driver_name ? f.driver_name : 'No Fleetly driver was driving'}</p>
            <p className="text-xs text-muted-foreground">
              Confirmed by {f.confirmed_by_name ?? 'unknown'}, {formatSastDateTime(f.confirmed_at)}
              {f.confirmation_note ? `. ${f.confirmation_note}` : ''}
            </p>
          </div>
        ) : (
          <p className="text-xs text-muted-foreground">Nobody has confirmed who was driving. The names below are proposals.</p>
        )}

        {blocked && (
          <p className="flex items-start gap-2 rounded-md border border-border bg-muted/50 px-3 py-2 text-xs text-muted-foreground">
            <ShieldAlert className="mt-0.5 h-3.5 w-3.5 shrink-0" /> {blocked}
          </p>
        )}

        <ul className="space-y-2">
          {detail.candidates.map((c) => (
            <li key={c.id} className="rounded-md border border-border px-3 py-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className="text-sm font-medium">{c.name ?? 'Nobody'}</span>
                <Chip tone={c.confidence === 'high' ? 'green' : c.confidence === 'medium' ? 'amber' : 'neutral'}>
                  {methodLabel(c.method)}{c.confidence ? `, ${c.confidence} confidence` : ''}
                </Chip>
                {c.is_you && <Chip tone="brand">This is you</Chip>}
                {c.driver_id && f.driver_id !== c.driver_id && f.entry_kind === 'notice' && (
                  <Button
                    size="sm"
                    variant="outline"
                    className="ml-auto h-7"
                    disabled={busy || !!blocked}
                    onClick={() => void run(confirm.trigger({ id: f.id, driver_id: c.driver_id, note: note || null }), `${c.name} confirmed as the driver.`)}
                  >
                    Confirm {c.name?.split(' ')[0]} was driving
                  </Button>
                )}
              </div>
              <p className="mt-1 text-xs text-muted-foreground">{c.basis}</p>
            </li>
          ))}
          {detail.candidates.length === 0 && <li className="text-xs text-muted-foreground">No proposal. The notice has no offence time, or the vehicle had no roster.</li>}
        </ul>

        {f.entry_kind === 'notice' && !blocked && (
          <div className="space-y-2 rounded-md border border-dashed border-border px-3 py-2">
            <p className="text-xs text-muted-foreground">
              Confirming a driver records who had the car. It deducts nothing from pay, and nothing ever will without the driver's signed consent for this fine.
            </p>
            <Textarea
              rows={2}
              value={note}
              onChange={(e) => setNote(e.target.value)}
              placeholder={f.confirmed_at ? 'Why the driver is changing (required)' : 'How you know (optional)'}
            />
            <div className="flex flex-wrap items-center gap-2">
              <NativeSelect className="h-8 max-w-xs text-xs" value={otherDriver} onChange={(e) => setOtherDriver(e.target.value)}>
                <option value="">Someone else…</option>
                {otherChoices.map((d) => (
                  <option key={d.id} value={d.id}>{d.name}{d.status !== 'active' ? ` (${d.status})` : ''}</option>
                ))}
              </NativeSelect>
              <Button
                size="sm"
                variant="outline"
                className="h-8"
                disabled={busy || !otherDriver}
                onClick={() => void run(confirm.trigger({ id: f.id, driver_id: otherDriver, note: note || null }), 'Driver confirmed.')}
              >
                Confirm this driver
              </Button>
              <Button
                size="sm"
                variant="ghost"
                className="h-8"
                disabled={busy}
                onClick={() => void run(confirm.trigger({ id: f.id, no_driver: true, note: note || null }), 'Recorded that no Fleetly driver was driving.')}
              >
                No Fleetly driver was driving
              </Button>
            </div>
          </div>
        )}
      </Section>

      {detail.can_decide_charged_to && f.entry_kind === 'notice' && (
        <Section title="Who carries it">
          <p className="text-xs text-muted-foreground">
            Until this is decided the owner statement credits nothing for this fine and says a decision is outstanding.
            Choosing the driver records who is liable, usually by nomination. It is never a deduction from pay.
          </p>
          <div className="flex flex-wrap gap-2">
            {(['driver', 'owner', 'fleetly'] as const).map((c) => (
              <Button
                key={c}
                size="sm"
                variant={f.charged_to === c ? 'brand' : 'outline'}
                className="h-8"
                disabled={busy || !!blocked || f.charged_to === c || (c === 'driver' && !f.driver_id)}
                onClick={() => void run(setCharged.trigger({ id: f.id, charged_to: c, note: null }), `${chargedToLabel(c)}.`)}
              >
                {c === 'driver' ? 'Driver' : c === 'owner' ? 'Owner' : 'Fleetly'}
              </Button>
            ))}
            {f.charged_to && (
              <Button size="sm" variant="ghost" className="h-8" disabled={busy || !!blocked}
                onClick={() => void run(setCharged.trigger({ id: f.id, charged_to: null, note: null }), 'Back to not decided.')}>
                Undo
              </Button>
            )}
          </div>
          {!f.driver_id && <p className="text-xs text-muted-foreground">The driver can be chosen once someone has confirmed who was driving.</p>}
        </Section>
      )}

      {f.entry_kind === 'notice' && (
        <Section title="Progress">
          <div className="flex flex-wrap gap-2">
            <Button size="sm" variant="outline" className="h-8" disabled={busy || !f.driver_id || f.status === 'nominated'}
              onClick={() => void run(setStatus.trigger({ id: f.id, status: 'nominated', note: statusNote || null }), 'Recorded as nominated.')}>
              Driver nominated
            </Button>
            <Button size="sm" variant="outline" className="h-8" disabled={busy || f.status === 'disputed'}
              onClick={() => void run(setStatus.trigger({ id: f.id, status: 'disputed', note: statusNote }), 'Recorded as disputed.')}>
              Disputed
            </Button>
            <Button size="sm" variant="outline" className="h-8" disabled={busy || f.status === 'written_off'}
              onClick={() => void run(setStatus.trigger({ id: f.id, status: 'written_off', note: statusNote }), 'Written off.')}>
              Write off
            </Button>
          </div>
          <Textarea rows={2} value={statusNote} onChange={(e) => setStatusNote(e.target.value)} placeholder="A reason (needed for disputed and written off)" />
          <div className="flex flex-wrap items-end gap-2">
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground" htmlFor="paid-amount">Paid, including any surcharge</label>
              <Input id="paid-amount" inputMode="decimal" className="h-8 w-32" value={paidAmount} onChange={(e) => setPaidAmount(e.target.value)} placeholder="R" />
            </div>
            <div className="space-y-1">
              <label className="text-[11px] text-muted-foreground" htmlFor="paid-on">Paid on</label>
              <Input id="paid-on" type="date" className="h-8 w-40" value={paidOn} onChange={(e) => setPaidOn(e.target.value)} />
            </div>
            <Button size="sm" variant="outline" className="h-8" disabled={busy || (f.source === 'manual' && !paidAmount)}
              onClick={() => void run(setStatus.trigger({ id: f.id, status: 'paid', note: statusNote || null, paid_amount_zar: paidAmount || null, paid_on: paidOn || null }), 'Recorded as paid.')}>
              Record payment
            </Button>
          </div>
          {f.paid_amount_zar !== null && (
            <p className="text-xs text-muted-foreground">Paid {formatZar(f.paid_amount_zar)} on {formatSastDateTime(f.paid_at)}.</p>
          )}
        </Section>
      )}

      <Section title="Record">
        <ul className="space-y-1 text-xs">
          {detail.events.map((e, i) => (
            <li key={i} className="flex flex-wrap gap-x-2 text-muted-foreground">
              <span className="tabular-nums">{formatSastDateTime(e.at)}</span>
              <span className="text-foreground">{eventLabel(e)}</span>
              {e.actor && <span>by {e.actor}</span>}
              {typeof e.detail?.note === 'string' && e.detail.note && <span>: {e.detail.note}</span>}
            </li>
          ))}
        </ul>
        <p className="text-xs text-muted-foreground">
          {f.created_by_name ? `Captured by ${f.created_by_name}` : sourceLabel(f.source)}, {formatSastDateTime(f.created_at)}.
          {f.source_ref ? ` Source reference ${f.source_ref}.` : ''}
        </p>
      </Section>

      {detail.can_delete && (
        <Section title="Delete this capture">
          <p className="text-xs text-muted-foreground">For a mistake or a test. The record of the deletion is kept.</p>
          <div className="flex flex-wrap items-center gap-2">
            <Input className="h-8 max-w-sm" value={deleteReason} onChange={(e) => setDeleteReason(e.target.value)} placeholder="Why it is being deleted" />
            <Button size="sm" variant="destructive" className="h-8" disabled={busy || !deleteReason.trim()} onClick={() => void onDelete()}>
              Delete
            </Button>
          </div>
        </Section>
      )}
    </div>
  )
}
